import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import Select from "components/common/Select";
import { toIsoUtc } from "../chiefComplaints/dateHelpers";
import ActivityService from "./ActivityService";
import {
  ACTIVITY_STATUS,
  activityStatusBand,
  MAX_DESCRIPTION_WORDS,
  countWords,
  clampWords,
} from "./data";
import { ACT_V2_REFRESH } from "./events";

/**
 * Status-only Update sheet for one activity — px-matched to Figma 3071:4666
 * (`Update status · Cycling`, 340 wide).
 *
 * This was UpdateStatusModal: a centred overlay on a scrim. The board draws
 * it as a flat bordered CARD with the chevron-up collapse in its header —
 * the rail idiom — so it now lives in the editor rail beside the Add/Edit
 * sheets, driven by the same useSheetStack. Detail fields (name, days,
 * minutes, intensity) are not editable here; the row-click Edit sheet is.
 *
 * Unlike an Add sheet it can never be blank, so it reports itself filled on
 * mount and claims no name for the cross-sheet duplicate check.
 */

const todayISO = () => new Date().toISOString().slice(0, 10);

/* Board 3071:4672 `icon/chev-up-16` — same collapse affordance as the
   Add/Edit sheet (see ActivityForm) and the addictions update card. */
const CollapseChevron = () => (
  <svg viewBox="0 0 11 11" width="16" height="16" fill="none" aria-hidden="true">
    <path
      d="M2 7.5L5.5 3.5L9 7.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ActivityStatusForm = ({
  patientId,
  formId,
  editing,
  ordinal,
  showOrdinal,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onClaimChange,
  onClose,
}) => {
  const activity = editing;

  const [status, setStatus] = useState(activity.status || "active");
  // Board 3071:4641 draws the date EMPTY in the pristine state — no silent
  // today-default (that was the old modal's behaviour). Backend treats
  // statusUpdate as optional, but the UI marks it required (red asterisk);
  // the Update button's canSubmit gate below enforces that contract so the
  // red asterisk isn't a lie.
  const [statusUpdate, setStatusUpdate] = useState("");
  // Prefill with the row's last-saved description so the clinician can
  // amend rather than retype. Starts blank only when nothing was saved.
  const [description, setDescription] = useState(activity.description || "");
  const [collapsed, setCollapsed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Capture the initial values ONCE so the dirty check compares against the
  // load-time snapshot even when the parent re-renders.
  const initialRef = useRef({
    status: activity.status || "active",
    statusUpdate: "",
    description: activity.description || "",
  });

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // Rail contract: an update sheet is never blank, and claims no name.
  useEffect(() => {
    onFilledChange(formId, true);
  }, [formId, onFilledChange]);
  useEffect(() => {
    onClaimChange(formId, null);
  }, [formId, onClaimChange]);

  // Dirty check — Update is a no-op unless at least one field differs from
  // what was loaded. Prevents an accidental click from writing an identical
  // audit row.
  const isDirty = useMemo(() => {
    const init = initialRef.current;
    if (status !== init.status) return true;
    if (statusUpdate !== init.statusUpdate) return true;
    if (description.trim() !== init.description.trim()) return true;
    return false;
  }, [status, statusUpdate, description]);

  const canSubmit = !!status
    && !!statusUpdate
    && statusUpdate >= "1900-01-01"
    && !submitting
    && !blockedBySibling
    && isDirty;

  const close = () => onClose(formId);

  const submit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setFeedback(null);
    onSavingChange(formId, true);
    const svc = new ActivityService();
    const done = () => {
      if (!mounted.current) return;
      onSavingChange(formId, false);
      setSubmitting(false);
      EventService.emit(ACT_V2_REFRESH);
      onClose(formId);
    };
    const failed = (err) => {
      // Unmounted means the rail already dropped this sheet and closeSheet
      // cleared the saving flag on the way out.
      if (!mounted.current) return;
      onSavingChange(formId, false);
      setSubmitting(false);
      // A version collision means the table on screen is stale — refresh it
      // alongside the error so the retry starts from what the server has.
      if (err && err.reason === "version") EventService.emit(ACT_V2_REFRESH);
      setFeedback({
        kind: "err",
        text: (err && err.message) || "Update failed — please retry",
      });
    };
    // Fresh version per mutation, never the one this sheet mounted with.
    // Siblings are blocked from saving while this is in flight, so the value
    // read here cannot go stale between the read and the write.
    svc.list(
      patientId,
      (doc) => {
        svc.updateStatus(
          patientId,
          activity.activityId,
          {
            status,
            statusUpdate: statusUpdate ? toIsoUtc(statusUpdate) : undefined,
            description: description.trim(),
            expectedVersion: (doc && doc.version) || 0,
          },
          done,
          failed
        );
      },
      failed
    );
  };

  const band = status ? activityStatusBand(status) : null;
  const foldId = `act-sfold-${formId}`;
  const statusId = `act-sstat-${formId}`;
  const dateId = `act-supd-${formId}`;
  const descId = `act-sdesc-${formId}`;

  return (
    <section className={`pa3-sdlg${collapsed ? " is-collapsed" : ""}`}>
      {/* Board 3071:4667 — a bare 24px header row, no divider beneath it. */}
      <header className="pa3-sdlg-head">
        {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
        {/* Two SIBLINGS spaced by the header's 8px gap, as the board authors
            them — nesting the sub runs the copy together in the text layer. */}
        <span className="pa3-sdlg-title">Update status</span>
        <span className="pa3-sdlg-sub">&middot; {activity.name}</span>
        {/* A chevron-up, not an x — the card-collapse affordance in this
            design system. Cancel in the footer is what closes the sheet. */}
        <button
          type="button"
          className="pa3-sdlg-chev"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls={foldId}
          aria-label={
            collapsed ? `Expand ${activity.name}` : `Collapse ${activity.name}`
          }
        >
          <CollapseChevron />
        </button>
      </header>

      <div className="pa3-sdlg-fold" id={foldId}>
        <div className="pa3-sdlg-fold-inner">
          {/* Board 3071:4673 — Status and its date sit 14 apart, tighter
              than the card's 20 rhythm. */}
          <div className="pa3-sdlg-group">
            <div
              className={`sec-field pa3-fld${band ? " has-dot" : ""}`}
              style={band ? { "--pa3-dot": band.dot } : undefined}
            >
              <label className="sec-label" htmlFor={statusId}>
                Status <span className="sec-label-required">*</span>
              </label>
              <Select
                id={statusId}
                value={status}
                options={ACTIVITY_STATUS}
                getKey={(o) => o.key}
                getLabel={(o) => o.label}
                onChange={setStatus}
              />
            </div>

            <div className="sec-field">
              <label className="sec-label" htmlFor={dateId}>
                Status update date <span className="sec-label-required">*</span>
              </label>
              <span className="pa3-sdate">
                <input
                  id={dateId}
                  className="sec-input"
                  type="date"
                  value={statusUpdate}
                  onChange={(e) => setStatusUpdate(e.target.value)}
                  /* min 1900-01-01 — anything earlier is almost certainly a
                     typo (medical records in a modern app don't go that far
                     back). HTML attr blocks the native picker; the
                     canSubmit gate below catches any manual typing that
                     bypasses it. */
                  min="1900-01-01"
                  max={todayISO()}
                  disabled={submitting}
                />
              </span>
            </div>
          </div>

          <div className="sec-field">
            <label className="sec-label" htmlFor={descId}>
              Description <span className="sec-label-note">optional</span>
              <span className="dx-word-hint">
                {countWords(description)} / {MAX_DESCRIPTION_WORDS} words
              </span>
            </label>
            <textarea
              id={descId}
              className="sec-textarea"
              rows={3}
              value={description}
              onChange={(e) =>
                setDescription(clampWords(e.target.value, MAX_DESCRIPTION_WORDS))
              }
              placeholder="Add a short note about this update…"
              disabled={submitting}
            />
          </div>

          {feedback && (
            <div className={`cc-feedback is-${feedback.kind}`}>{feedback.text}</div>
          )}

          {/* Board 3071:4696 — equal-width 36px r8 buttons, 10px apart. */}
          <footer className="pa3-sdlg-foot">
            {/* Never disabled while saving: a write that never settles would
                otherwise pin the sheet open with no way out. */}
            <button type="button" className="sec-btn sec-btn-ghost" onClick={close}>
              {submitting ? "Close" : "Cancel"}
            </button>
            <button
              type="button"
              className="sec-btn sec-btn-primary"
              onClick={submit}
              disabled={!canSubmit}
              title={
                blockedBySibling
                  ? "Another activity is saving"
                  : !isDirty
                  ? "No changes to save"
                  : undefined
              }
            >
              {submitting ? "Saving…" : "Update"}
            </button>
          </footer>
        </div>
      </div>
    </section>
  );
};

ActivityStatusForm.propTypes = {
  patientId: PropTypes.number,
  formId: PropTypes.number.isRequired,
  /** The activity whose status is being updated (carries the mode marker). */
  editing: PropTypes.object.isRequired,
  ordinal: PropTypes.number,
  showOrdinal: PropTypes.bool,
  blockedBySibling: PropTypes.bool,
  onFilledChange: PropTypes.func.isRequired,
  onSavingChange: PropTypes.func.isRequired,
  onClaimChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ActivityStatusForm;
